import {
  type AiResponse,
  type CatalogProductCard,
  type EmailDraftCardData,
  type EmailDraftCardResponse,
  type OrchestrationResetResponse,
  type OutfitProduct,
  type SuggestedAction,
} from '../../types/ai';
import type { AnnouncementBanner } from '../../types/announcements';
import { maybeFixMojibake } from '../../utils/mojibake';

import { toAiResponse } from './ai-response';
import { repairSuggestedActions } from './chat-protocol';
import { apiClient } from './client';
import { shouldForwardStatus, type StatusEmitState } from './status-dedupe';
import { readSseRecords, type SseRecord } from './sse-stream';

export type ChatRequest = {
  message: string;
  image?: { dataUrl: string; mimeType: string };
  channel?: 'playground' | 'website';
  silent?: boolean;
  source?: string;
  suggestion?: SuggestedAction;
  metadata?: Record<string, unknown>;
  conversationHistory?: Array<{ role: 'user' | 'assistant'; content: string }>;
  postalCode?: string;
  cid?: string;
  sessionId?: string;
  clientCapabilities?: string[];
};

export type StreamCallback = (chunk: string) => void;
export type StatusCallback = (status: { key: string; text: string }) => void;
export type SuggestedActionsCallback = (actions: SuggestedAction[]) => void;
export type OutfitSlotReadyCallback = (event: {
  slotKey: string;
  product: OutfitProduct;
  totalSlots: number;
}) => void;
export type ProductCardReadyCallback = (event: {
  card: OutfitProduct;
  categoryId?: string;
  categoryLabel?: string;
  totalExpected?: number;
}) => void;
export type HandoffStatusCallback = (status: string) => void;
export type EmailDraftCallback = (card: EmailDraftCardData) => void;
export type AnnouncementCallback = (banner: AnnouncementBanner) => void;

// Namngivna i stället för positionella: listan har vuxit sju gånger sedan
// strömningen kom, och varje tillägg mitt i en parameterlista flyttar allt
// efter den hos varje anropare.
export type ChatStreamHandlers = {
  onChunk: StreamCallback;
  onStatus?: StatusCallback;
  onSuggestedActions?: SuggestedActionsCallback;
  onOutfitSlotReady?: OutfitSlotReadyCallback;
  onProductCardReady?: ProductCardReadyCallback;
  onHandoffStatus?: HandoffStatusCallback;
  onEmailDraft?: EmailDraftCallback;
  onAnnouncement?: AnnouncementCallback;
};

const REQUEST_TIMEOUT_MS = 120_000;

const isHandoffDebugEnabled = (): boolean => {
  if (!import.meta.env.DEV) {
    return false;
  }

  if (typeof window === 'undefined') {
    return false;
  }

  const params = new URLSearchParams(window.location.search);
  return params.get('devtools') === '1';
};

export const sendChatStream = async (
  payload: ChatRequest,
  {
    onChunk,
    onStatus,
    onSuggestedActions,
    onOutfitSlotReady,
    onProductCardReady,
    onHandoffStatus,
    onEmailDraft,
    onAnnouncement,
  }: ChatStreamHandlers,
): Promise<AiResponse> => {
  const endpoint =
    payload.channel === 'playground' || payload.channel === 'website'
      ? '/ai/chat/website'
      : '/ai/chat';

  const baseURL = apiClient.defaults.baseURL || '/api';
  const url = `${baseURL}${endpoint}`;

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => {
    controller.abort(new Error('SSE stream timeout'));
  }, REQUEST_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      credentials: 'include',
      signal: controller.signal,
    });
  } catch (error) {
    if (controller.signal.aborted) {
      throw new Error('Stream timeout: inget svar från servern i tid.');
    }
    throw error;
  }

  if (!response.ok) {
    const errorText = await response.text();
    console.error('SSE stream error:', {
      status: response.status,
      statusText: response.statusText,
      body: errorText,
    });
    throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
  }

  if (!response.body) {
    throw new Error('No response body');
  }

  let fullMessage = '';
  let metadata: Partial<AiResponse> = {};
  let productCards: CatalogProductCard[] | undefined;
  const statusEmitState: StatusEmitState = { lastAnyAt: 0, byKey: new Map() };

  try {
    for await (const record of readSseRecords(response.body)) {
      const event = record as SseRecord & {
        content?: unknown;
        key?: unknown;
        text?: unknown;
        message?: unknown;
        productCards?: unknown;
        status?: unknown;
        actions?: SuggestedAction[];
        data?: Record<string, unknown>;
        draft?: Record<string, unknown>;
        labels?: { send?: unknown };
        id?: unknown;
        style?: unknown;
        color?: unknown;
        link?: { url?: unknown; label?: unknown };
      };

      if (event.type === 'content') {
        const chunk = maybeFixMojibake(String(event.content ?? ''));
        fullMessage += chunk;
        onChunk(chunk);
      } else if (event.type === 'status') {
        if (typeof event.key === 'string' && typeof event.text === 'string') {
          const statusEvent = { key: event.key, text: event.text };
          if (shouldForwardStatus(statusEmitState, statusEvent)) {
            onStatus?.(statusEvent);
          }
        }
      } else if (event.type === 'metadata') {
        metadata = {
          ...(event as Partial<AiResponse>),
          message: typeof event.message === 'string' ? maybeFixMojibake(event.message) : undefined,
        };
        if (Array.isArray(event.productCards)) {
          productCards = event.productCards as CatalogProductCard[];
        }
      } else if (event.type === 'handoff_status') {
        if (typeof event.status === 'string') {
          onHandoffStatus?.(event.status);
        }
        if (event.status === 'processing' && isHandoffDebugEnabled()) {
          alert('Handoff: Genererar sammanfattning och mail till kundtjänst...');
        }
      } else if (event.type === 'handoff_summary') {
        if (isHandoffDebugEnabled()) {
          alert('HANDOFF SAMMANFATTNING (AI):\n\n' + String(event.content));
        }
      } else if (event.type === 'suggested_actions') {
        onSuggestedActions?.(repairSuggestedActions(event.actions ?? []));
      } else if (event.type === 'outfit_slot_ready') {
        const slotKey = typeof event.data?.slotKey === 'string' ? event.data.slotKey : undefined;
        const product = event.data?.product as OutfitProduct | undefined;
        const totalSlots = typeof event.data?.totalSlots === 'number' ? event.data.totalSlots : 0;
        if (slotKey && product) {
          onOutfitSlotReady?.({ slotKey, product, totalSlots });
        }
      } else if (event.type === 'product_card_ready') {
        const card = event.data?.card as OutfitProduct | undefined;
        const categoryId =
          typeof event.data?.categoryId === 'string' ? event.data.categoryId : undefined;
        const categoryLabel =
          typeof event.data?.categoryLabel === 'string' ? event.data.categoryLabel : undefined;
        const totalExpected =
          typeof event.data?.totalExpected === 'number' ? event.data.totalExpected : undefined;
        if (card) {
          onProductCardReady?.({ card, categoryId, categoryLabel, totalExpected });
        }
      } else if (event.type === 'email_draft') {
        const draft = event.draft;
        const labels = event.labels;
        if (
          draft &&
          typeof draft.id === 'string' &&
          typeof draft.subject === 'string' &&
          typeof draft.body === 'string' &&
          labels &&
          typeof labels.send === 'string'
        ) {
          onEmailDraft?.({
            draft: {
              id: draft.id,
              status: String(draft.status ?? 'drafted'),
              subject: maybeFixMojibake(draft.subject),
              body: maybeFixMojibake(draft.body),
            },
            labels: labels as EmailDraftCardData['labels'],
          });
        }
      } else if (event.type === 'handoff_debug') {
        if (isHandoffDebugEnabled()) {
          alert(
            'HANDOFF DEBUG (Detta mail skickades till kundtjänst):\n\n' + String(event.content),
          );
        }
      } else if (event.type === 'announcement') {
        if (typeof event.text === 'string' && typeof event.style === 'string') {
          const link = event.link;
          onAnnouncement?.({
            id: String(event.id ?? ''),
            text: event.text,
            style: event.style as AnnouncementBanner['style'],
            color: typeof event.color === 'string' ? event.color : undefined,
            link:
              link && typeof link.url === 'string' && typeof link.label === 'string'
                ? { url: link.url, label: link.label }
                : undefined,
          });
        }
      }
    }
  } finally {
    window.clearTimeout(timeoutId);
  }

  return toAiResponse({
    metadata,
    streamedMessage: fullMessage,
    productCards,
    requestChannel: payload.channel,
  });
};

export const resetChatOrchestration = async (payload: {
  sessionId: string;
  customerIdHash?: string | null;
}) => {
  const endpoint = '/ai/chat/website/session/orchestration-reset';
  const { data } = await apiClient.post<OrchestrationResetResponse>(endpoint, payload);
  return data;
};

export const confirmEmailDraft = async (payload: {
  sessionId: string;
  customerIdHash?: string | null;
  subject: string;
  body: string;
  email?: string;
}): Promise<EmailDraftCardResponse> => {
  const { data } = await apiClient.post<EmailDraftCardResponse>(
    '/ai/chat/website/email-draft/confirm',
    { customerIdHash: null, ...payload },
  );
  return data;
};

export const cancelEmailDraft = async (payload: {
  sessionId: string;
  customerIdHash?: string | null;
}): Promise<EmailDraftCardResponse> => {
  const { data } = await apiClient.post<EmailDraftCardResponse>(
    '/ai/chat/website/email-draft/cancel',
    { customerIdHash: null, ...payload },
  );
  return data;
};
